import type { AuthState, NbaScope } from "../context/types";
import type { SessionStorageAdapter } from "./session";

export interface NbaAttributionIdentity {
  authState: AuthState;
  userId?: string;
}
export interface NbaRecommendationAttribution {
  actionId: string;
  actionType: "start-course" | "view-roadmap" | "view-page" | "show-offer";
  ruleId: string;
  scope: NbaScope;
  source: "client-recommendation-click";
  sourcePage: string;
  clickedAt: string;
  courseSlug?: string;
  roadmapSlug?: string;
  experimentId?: string;
  variantId?: string;
}
export type NbaAttributionCandidate = Omit<
  NbaRecommendationAttribution,
  "scope" | "source" | "clickedAt"
>;
export type NbaAttributionTarget =
  | { courseSlug: string; roadmapSlug?: never }
  | { roadmapSlug: string; courseSlug?: never };
export interface NbaAttributionStoreOptions {
  scope: NbaScope;
  storage?: SessionStorageAdapter;
  now: () => string;
  identity: NbaAttributionIdentity;
}
export interface NbaAttributionStore {
  /** Synchronous once initialization resolves, so a click can persist before navigation. */
  record(candidate: NbaAttributionCandidate): boolean;
  read(target: NbaAttributionTarget): NbaRecommendationAttribution | null;
  clear(): void;
}
interface Envelope {
  version: 1;
  scope: NbaScope;
  salt: string;
  owner: string;
  entries: NbaRecommendationAttribution[];
}
export const NBA_ATTRIBUTION_TTL_MS = 30 * 60_000;
export const NBA_ATTRIBUTION_LIMIT = 20;
const IDENTIFIER = /^[a-zA-Z0-9][a-zA-Z0-9_.:-]{0,127}$/;
const initialization = new WeakMap<SessionStorageAdapter, Promise<unknown>>();
const disabled: NbaAttributionStore = {
  record: () => false,
  read: () => null,
  clear() {},
};
const isIdentifier = (value: unknown): value is string =>
  typeof value === "string" && IDENTIFIER.test(value);
const keyFor = (scope: NbaScope) => `yumesaku:nba:attribution:v1:${scope}`;

function cleanEntry(
  value: unknown,
  scope: NbaScope,
  now: number,
): NbaRecommendationAttribution | null {
  if (!["courses", "lab", "women"].includes(scope)) return null;
  if (!value || typeof value !== "object" || !Number.isFinite(now)) return null;
  const row = value as Partial<NbaRecommendationAttribution>;
  if (
    row.scope !== scope ||
    row.source !== "client-recommendation-click" ||
    !isIdentifier(row.actionId) ||
    !isIdentifier(row.ruleId)
  )
    return null;
  if (
    !["start-course", "view-roadmap", "view-page", "show-offer"].includes(
      row.actionType ?? "",
    )
  )
    return null;
  const clicked =
    typeof row.clickedAt === "string" ? Date.parse(row.clickedAt) : NaN;
  if (
    !Number.isFinite(clicked) ||
    clicked > now ||
    now - clicked >= NBA_ATTRIBUTION_TTL_MS
  )
    return null;
  const path =
    typeof row.sourcePage === "string" ? row.sourcePage.split(/[?#]/)[0] : "";
  if (!/^\/(?!\/)[a-zA-Z0-9/_-]*$/.test(path) || path.length > 300) return null;
  const courseSlug = isIdentifier(row.courseSlug) ? row.courseSlug : undefined;
  const roadmapSlug = isIdentifier(row.roadmapSlug)
    ? row.roadmapSlug
    : undefined;
  if (!courseSlug && !roadmapSlug) return null;
  if (row.actionType === "start-course" && !courseSlug) return null;
  if (row.actionType === "view-roadmap" && !roadmapSlug) return null;
  const experiment =
    isIdentifier(row.experimentId) && isIdentifier(row.variantId)
      ? { experimentId: row.experimentId, variantId: row.variantId }
      : {};
  return {
    actionId: row.actionId,
    actionType: row.actionType as NbaRecommendationAttribution["actionType"],
    ruleId: row.ruleId,
    scope,
    source: "client-recommendation-click",
    sourcePage: path,
    clickedAt: new Date(clicked).toISOString(),
    ...(courseSlug ? { courseSlug } : {}),
    ...(roadmapSlug ? { roadmapSlug } : {}),
    ...experiment,
  };
}

function parseEnvelope(
  raw: string | null,
  scope: NbaScope,
  now: number,
): Envelope | null {
  if (!raw || raw.length > 40_000) return null;
  try {
    const value = JSON.parse(raw) as Envelope;
    if (
      value.version !== 1 ||
      value.scope !== scope ||
      typeof value.salt !== "string" ||
      !/^[a-f0-9]{32}$/.test(value.salt) ||
      typeof value.owner !== "string" ||
      !/^[a-f0-9]{64}$/.test(value.owner) ||
      !Array.isArray(value.entries)
    )
      return null;
    return {
      version: 1,
      scope,
      salt: value.salt,
      owner: value.owner,
      entries: value.entries
        .slice(-NBA_ATTRIBUTION_LIMIT)
        .map((entry) => cleanEntry(entry, scope, now))
        .filter(
          (entry): entry is NbaRecommendationAttribution => entry !== null,
        ),
    };
  } catch {
    return null;
  }
}

async function createStore(
  options: NbaAttributionStoreOptions,
): Promise<NbaAttributionStore> {
  const { identity, scope, storage } = options;
  // Unresolved authentication must not read another account's history or clear it during loading.
  if (
    !["guest", "authenticated"].includes(identity.authState) ||
    (identity.authState === "authenticated" &&
      (!identity.userId || identity.userId.length > 512))
  )
    return disabled;
  if (!globalThis.crypto?.subtle || !Number.isFinite(Date.parse(options.now())))
    return disabled;
  const key = keyFor(scope);
  const now = () => Date.parse(options.now());
  const load = () => parseEnvelope(storage?.getItem(key) ?? null, scope, now());
  let stored: Envelope | null = null;
  try {
    stored = load();
  } catch {
    /* Optional storage. */
  }
  const digest = async (salt: string) => {
    const bytes = new TextEncoder().encode(
      `${salt}\u0000${identity.authState}\u0000${identity.authState === "authenticated" ? identity.userId : ""}`,
    );
    return Array.from(
      new Uint8Array(await globalThis.crypto.subtle.digest("SHA-256", bytes)),
      (byte) => byte.toString(16).padStart(2, "0"),
    ).join("");
  };
  const randomSalt = () =>
    Array.from(globalThis.crypto.getRandomValues(new Uint8Array(16)), (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");
  let state: Envelope;
  if (stored && stored.owner === (await digest(stored.salt))) state = stored;
  else {
    const salt = randomSalt();
    state = { version: 1, scope, salt, owner: await digest(salt), entries: [] };
  }
  const boundOwner = state.owner;
  let persisted = false;
  const persist = () => {
    if (!storage) return;
    try {
      storage.setItem(key, JSON.stringify(state));
      persisted = true;
    } catch {
      /* Memory-only fallback. */
    }
  };
  persist();
  const refresh = (): boolean => {
    if (storage) {
      try {
        const current = load();
        // A stale page runtime must never overwrite another account or resurrect cleared history.
        if (
          (current && current.owner !== boundOwner) ||
          (!current && persisted)
        )
          return false;
        if (current) state = current;
      } catch {
        /* In-memory use is still possible when storage is blocked. */
      }
    }
    state.entries = state.entries.filter(
      (entry) => cleanEntry(entry, scope, now()) !== null,
    );
    return true;
  };
  return {
    record(candidate) {
      if (!refresh()) return false;
      const entry = cleanEntry(
        {
          ...candidate,
          scope,
          source: "client-recommendation-click",
          clickedAt: options.now(),
        },
        scope,
        now(),
      );
      if (!entry) return false;
      state.entries = [
        ...state.entries.filter(
          (previous) => previous.actionId !== entry.actionId,
        ),
        entry,
      ].slice(-NBA_ATTRIBUTION_LIMIT);
      persist();
      return true;
    },
    read(target) {
      if (!refresh() || !!target.courseSlug === !!target.roadmapSlug)
        return null;
      const id = target.courseSlug ?? target.roadmapSlug;
      if (!isIdentifier(id)) return null;
      const entry = [...state.entries]
        .reverse()
        .find((entry) =>
          target.courseSlug
            ? entry.courseSlug === target.courseSlug
            : entry.roadmapSlug === target.roadmapSlug,
        );
      return entry ? { ...entry } : null;
    },
    clear() {
      if (!refresh()) return;
      state.entries = [];
      persist();
    },
  };
}

/** Stores a salted account fingerprint, never the raw ID. Known auth/account changes start an empty store. */
export function createNbaAttributionStore(
  options: NbaAttributionStoreOptions,
): Promise<NbaAttributionStore> {
  // Serialize async identity binding across runtimes sharing one browser storage adapter.
  const previous = options.storage
    ? initialization.get(options.storage)
    : undefined;
  const pending = (previous ?? Promise.resolve())
    .catch(() => undefined)
    .then(() => createStore(options))
    .catch(() => disabled);
  if (options.storage) initialization.set(options.storage, pending);
  return pending;
}

/** Optional client attribution only: callers emit their existing success event after the server confirms it. */
export async function readNbaAttribution(
  options: NbaAttributionStoreOptions & NbaAttributionTarget,
): Promise<NbaRecommendationAttribution | null> {
  return (await createNbaAttributionStore(options)).read(options);
}

/** Prefixes distinguish client attribution from authoritative event/evidence fields. */
export function nbaAttributionProperties(
  attribution: NbaRecommendationAttribution | null,
): Record<string, string> {
  if (!attribution) return {};
  attribution = cleanEntry(
    attribution,
    attribution.scope,
    Date.parse(attribution.clickedAt),
  );
  if (!attribution) return {};
  return {
    nba_action_id: attribution.actionId,
    nba_action_type: attribution.actionType,
    nba_rule_id: attribution.ruleId,
    nba_scope: attribution.scope,
    nba_source_page: attribution.sourcePage,
    nba_clicked_at: attribution.clickedAt,
    nba_attribution_source: "client-recommendation-click",
    ...(attribution.courseSlug
      ? { nba_course_slug: attribution.courseSlug }
      : {}),
    ...(attribution.roadmapSlug
      ? { nba_roadmap_slug: attribution.roadmapSlug }
      : {}),
    ...(attribution.experimentId && attribution.variantId
      ? {
          nba_experiment_id: attribution.experimentId,
          nba_variant_id: attribution.variantId,
        }
      : {}),
  };
}

/** Correlation inputs for evaluateDecision; this intentionally supplies no OutcomeEvidence. */
export function nbaAttributionEvaluationContext(
  attribution: NbaRecommendationAttribution,
) {
  return {
    expectedActionId: attribution.actionId,
    expectedRuleId: attribution.ruleId,
    ...(attribution.experimentId && attribution.variantId
      ? {
          expectedExperimentId: attribution.experimentId,
          expectedVariantId: attribution.variantId,
        }
      : {}),
  };
}
